import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const links = [
    { label: "Problem", id: "problem" },
    { label: "Analysis", id: "friction" },
    { label: "Process", id: "process" },
    { label: "Results", id: "comparison" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled ? "bg-card/95 backdrop-blur-sm shadow-soft-md border-b border-border" : "bg-transparent"
        }`}
      >
        <div className="container-wide">
          <nav className="flex items-center justify-between h-12 md:h-14">
            <button onClick={() => scrollTo("intro")} className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading font-bold text-xs">C</span>
              </div>
              <span className="font-heading font-semibold text-foreground text-sm">Clarity</span>
            </button>

            <div className="hidden md:flex items-center gap-0.5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-secondary/50"
                >
                  {l.label}
                </button>
              ))}
            </div>

            <div className="hidden md:block">
              <Button variant="clarity" size="sm" onClick={() => scrollTo("contact")} className="text-xs h-8">
                Discuss Your Product
              </Button>
            </div>

            <button
              className="md:hidden p-1.5 text-muted-foreground"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-foreground/10 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-56 bg-card shadow-soft-xl border-l border-border"
            >
              <div className="p-4 pt-14">
                {links.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => scrollTo(l.id)}
                    className="block w-full text-left px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded transition-colors"
                  >
                    {l.label}
                  </button>
                ))}
                <div className="pt-3 mt-3 border-t border-border">
                  <Button variant="clarity" className="w-full text-sm" onClick={() => scrollTo("contact")}>
                    Discuss Your Product
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
